import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Link as LinkIcon, RefreshCw, Trash2 } from "lucide-react";
import type { PlaidItem, PlaidStatus } from "@finance/shared";

import { LastUpdated } from "@/components/data/last-updated";
import { SyncStatusPill } from "@/components/data/sync-status-pill";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ApiError } from "@/lib/api";
import {
  createPlaidLinkToken,
  exchangePlaidPublicToken,
  fetchPlaidStatus,
  listPlaidItems,
  syncPlaidItem,
} from "@/lib/api";
import { loadPlaidLink, type PlaidLinkAccountMetadata } from "@/lib/plaid-link";
import { DeletePlaidItemDialog } from "@/components/settings/delete-plaid-item-dialog";
import { PlaidAccountSelectDialog } from "@/components/settings/plaid-account-select-dialog";

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; status: PlaidStatus; items: PlaidItem[] }
  | { kind: "error"; message: string };

interface ToastState {
  tone: "success" | "info";
  message: string;
}

// Held while the user is in the in-app Account Select step: Plaid Link has
// already returned (we own the public_token) but the user hasn't picked
// which accounts to keep yet. Tracked here rather than inside the dialog so
// the Plaid SDK handler can be destroyed once and reused on cancel.
interface PendingExchange {
  publicToken: string;
  institutionName: string;
  accounts: PlaidLinkAccountMetadata[];
}

export function PlaidConnectPanel() {
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PlaidItem | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [pendingExchange, setPendingExchange] = useState<PendingExchange | null>(
    null,
  );
  const [exchangingPick, setExchangingPick] = useState(false);
  const [pickError, setPickError] = useState<string | null>(null);

  // Auto-dismiss the inline confirmation so the strip doesn't grow forever
  // after a chain of actions.
  useEffect(() => {
    if (!toast) return;
    const handle = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(handle);
  }, [toast]);

  const reload = useCallback((signal?: AbortSignal) => {
    setState((prev) =>
      prev.kind === "ready" ? prev : { kind: "loading" },
    );
    return Promise.all([fetchPlaidStatus(signal), safeListItems(signal)])
      .then(([status, items]) => {
        if (signal?.aborted) return;
        setState({ kind: "ready", status, items });
      })
      .catch((err: unknown) => {
        if (signal?.aborted) return;
        setState({
          kind: "error",
          message:
            err instanceof Error ? err.message : "Failed to load Plaid status",
        });
      });
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    void reload(ctrl.signal);
    return () => ctrl.abort();
  }, [reload]);

  // Fallback exchange path used only when Plaid Link returns no accounts
  // metadata — defensive, since `metadata.accounts` is documented to carry
  // every account the user is connecting. Sends no whitelist so the row
  // lands with `selected_account_ids = NULL` (sync-all).
  const runExchangeDirect = useCallback(
    async (publicToken: string, institutionName: string) => {
      try {
        await exchangePlaidPublicToken({
          public_token: publicToken,
          institution_name: institutionName,
        });
        await reload();
      } catch (err) {
        setLinkError(
          err instanceof Error
            ? err.message
            : "Failed to finish Plaid connection",
        );
      } finally {
        setLinking(false);
      }
    },
    [reload],
  );

  const startLink = useCallback(async () => {
    setLinking(true);
    setLinkError(null);
    try {
      const [linkToken, plaid] = await Promise.all([
        createPlaidLinkToken(),
        loadPlaidLink(),
      ]);
      const handler = plaid.create({
        token: linkToken.link_token,
        onSuccess: (publicToken, metadata) => {
          // FIN-38: Don't exchange yet — Plaid Link's hosted Account Select
          // UI only renders when the operator has created a Dashboard Link
          // Customization with Account Select enabled. Until then, Link
          // hands back every account in the institution with no opportunity
          // to deselect. Stash the token + metadata and let the user pick
          // in our own dialog before the API call.
          handler.destroy();
          const institutionName = metadata.institution?.name ?? "Unknown";
          const accounts = metadata.accounts ?? [];
          if (accounts.length === 0) {
            // Defensive: Plaid almost always returns at least one entry
            // here, but if metadata is empty there's nothing to pick from.
            // Exchange directly without a whitelist (server stores NULL,
            // sync syncs everything) so the user isn't stuck on a blank
            // dialog.
            void runExchangeDirect(publicToken, institutionName);
            return;
          }
          setPendingExchange({ publicToken, institutionName, accounts });
          setPickError(null);
          setLinking(false);
        },
        onExit: (err) => {
          setLinking(false);
          if (err) {
            setLinkError(err.display_message ?? err.error_message);
          }
          handler.destroy();
        },
      });
      handler.open();
    } catch (err) {
      setLinking(false);
      const fallback = "Failed to start Plaid Link";
      if (err instanceof ApiError && err.status === 503) {
        setLinkError(messageForPlaid503(err));
        await reload();
        return;
      }
      setLinkError(err instanceof Error ? err.message : fallback);
    }
  }, [reload, runExchangeDirect]);

  const confirmPick = useCallback(
    async (selectedIds: string[]) => {
      if (!pendingExchange || selectedIds.length === 0) return;
      setExchangingPick(true);
      setPickError(null);
      try {
        const picked = new Set(selectedIds);
        const accounts = pendingExchange.accounts
          .filter((a) => picked.has(a.id))
          .map((a) => ({
            id: a.id,
            name: a.name,
            mask: a.mask,
            type: a.type,
            subtype: a.subtype,
          }));
        await exchangePlaidPublicToken({
          public_token: pendingExchange.publicToken,
          institution_name: pendingExchange.institutionName,
          // FIN-31 / FIN-38: forward only the accounts the user kept
          // selected. The API persists this list as `selected_account_ids`
          // and every sync route re-filters by it, so deselected accounts
          // never reach `bank_accounts` / `credit_cards` / `transactions`.
          accounts,
        });
        setPendingExchange(null);
        await reload();
      } catch (err) {
        setPickError(
          err instanceof Error
            ? err.message
            : "Failed to finish Plaid connection",
        );
      } finally {
        setExchangingPick(false);
      }
    },
    [pendingExchange, reload],
  );

  const cancelPick = useCallback(() => {
    if (exchangingPick) return;
    setPendingExchange(null);
    setPickError(null);
  }, [exchangingPick]);

  const sync = useCallback(
    async (item: PlaidItem) => {
      setSyncingId(item.item_id);
      try {
        await syncPlaidItem(item.item_id);
        await reload();
      } catch (err) {
        setLinkError(
          err instanceof Error ? err.message : "Failed to sync Plaid item",
        );
      } finally {
        setSyncingId(null);
      }
    },
    [reload],
  );

  const body = useMemo(() => {
    if (state.kind === "loading") {
      return (
        <Card>
          <CardContent className="space-y-3 p-6">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="h-10 animate-pulse rounded-md bg-muted/60" />
            ))}
          </CardContent>
        </Card>
      );
    }
    if (state.kind === "error") {
      return <ErrorCard message={state.message} />;
    }
    return (
      <div className="space-y-4">
        {!state.status.configured ? (
          <NotConfiguredCard environment={state.status.environment} />
        ) : (
          <ConnectCard
            environment={state.status.environment}
            onConnect={startLink}
            connecting={linking}
            error={linkError}
            hasItems={state.items.length > 0}
          />
        )}
        {toast ? (
          <p
            role="status"
            className="rounded-md border border-success/40 bg-success/10 px-3 py-2 text-xs text-success"
          >
            {toast.message}
          </p>
        ) : null}
        <ItemsList
          items={state.items}
          syncingId={syncingId}
          onSync={sync}
          onDelete={(item) => setDeleteTarget(item)}
          configured={state.status.configured}
        />
      </div>
    );
  }, [state, linking, linkError, syncingId, startLink, sync, toast]);

  return (
    <>
      {body}
      <DeletePlaidItemDialog
        open={deleteTarget !== null}
        item={deleteTarget}
        onOpenChange={(next) => {
          if (!next) setDeleteTarget(null);
        }}
        onSuccess={() => {
          setToast({ tone: "success", message: "Plaid connection removed." });
          void reload();
        }}
      />
      <PlaidAccountSelectDialog
        open={pendingExchange !== null}
        institutionName={pendingExchange?.institutionName ?? ""}
        accounts={pendingExchange?.accounts ?? []}
        submitting={exchangingPick}
        error={pickError}
        onConfirm={confirmPick}
        onCancel={cancelPick}
      />
    </>
  );
}

async function safeListItems(signal?: AbortSignal): Promise<PlaidItem[]> {
  try {
    return await listPlaidItems(signal);
  } catch {
    return [];
  }
}

// The Worker returns 503 for two distinct cases — secrets missing
// (`plaid_not_configured`) and Plaid rejecting otherwise-present credentials
// (`plaid_credentials_rejected`). Conflating both as "not configured" hides
// credential/environment mismatches behind a misleading setup prompt, so we
// branch on the body's `error` code (carried as `ApiError.message`).
function messageForPlaid503(err: ApiError): string {
  if (err.message === "plaid_credentials_rejected") {
    return (
      err.serverMessage ??
      "Plaid rejected the configured client_id/secret. Confirm the credentials match the targeted Plaid environment."
    );
  }
  if (err.message === "plaid_not_configured") {
    return (
      err.serverMessage ??
      "Plaid is not configured on the Worker. Set PLAID_CLIENT_ID, PLAID_SECRET, and PLAID_ENCRYPTION_KEY on the finance-api Worker (not Pages), then try again."
    );
  }
  return err.serverMessage ?? "Plaid request failed (503).";
}

function NotConfiguredCard({
  environment,
}: {
  environment: PlaidStatus["environment"];
}) {
  return (
    <Card className="border-warning/40 bg-warning/5">
      <CardContent className="flex items-start gap-3 py-6">
        <AlertTriangle
          className="mt-0.5 h-5 w-5 text-warning"
          aria-hidden
        />
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">
            Plaid not configured
          </p>
          <p className="max-w-prose text-sm text-muted-foreground">
            Set <code className="font-mono text-xs">PLAID_CLIENT_ID</code> and{" "}
            <code className="font-mono text-xs">PLAID_SECRET</code> (plus{" "}
            <code className="font-mono text-xs">PLAID_ENCRYPTION_KEY</code> for
            at-rest encryption) on the <strong>finance-api Worker</strong>{" "}
            (not on Cloudflare Pages — Pages serves only static assets) to
            enable bank, credit card, and brokerage sync. See{" "}
            <code className="font-mono text-xs">apps/api/README.md</code> for
            setup steps.
          </p>
          {environment ? (
            <p className="font-mono text-xs text-muted-foreground">
              Target environment when configured: {environment}
            </p>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

function ConnectCard({
  environment,
  onConnect,
  connecting,
  error,
  hasItems,
}: {
  environment: PlaidStatus["environment"];
  onConnect: () => void;
  connecting: boolean;
  error: string | null;
  hasItems: boolean;
}) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-foreground">
              Connect with Plaid
            </p>
            {environment ? (
              <Badge variant="outline" className="font-mono text-[10px] uppercase">
                {environment}
              </Badge>
            ) : null}
          </div>
          <p className="max-w-prose text-sm text-muted-foreground">
            {hasItems
              ? "Add another institution. We never store your bank credentials — only an encrypted Plaid access token."
              : "Securely link a bank, credit card, or brokerage. We never store your bank credentials — only an encrypted Plaid access token."}
          </p>
          {error ? (
            <p className="font-mono text-xs text-destructive" role="alert">
              {error}
            </p>
          ) : null}
        </div>
        <Button onClick={onConnect} disabled={connecting}>
          <LinkIcon className="h-4 w-4" aria-hidden />
          {connecting ? "Opening Plaid…" : "Connect institution"}
        </Button>
      </CardContent>
    </Card>
  );
}

function ItemsList({
  items,
  syncingId,
  onSync,
  onDelete,
  configured,
}: {
  items: PlaidItem[];
  syncingId: string | null;
  onSync: (item: PlaidItem) => void;
  onDelete: (item: PlaidItem) => void;
  configured: boolean;
}) {
  if (items.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          {configured
            ? "No institutions connected yet."
            : "Connect an institution after Plaid is configured to see it here."}
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardContent className="divide-y divide-border p-0">
        {items.map((item) => (
          <div
            key={item.id}
            className="flex flex-col gap-3 px-6 py-4 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="min-w-0 space-y-1">
              <p className="truncate text-sm font-medium text-foreground">
                {item.institution_name}
              </p>
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <Badge
                  variant="outline"
                  className="font-mono text-[10px] uppercase tracking-wide"
                >
                  {item.status}
                </Badge>
                <SyncStatusPill
                  source="api"
                  lastSyncedAt={item.last_synced_at}
                  lastSyncStatus={null}
                />
                <span>
                  Last synced: <LastUpdated iso={item.last_synced_at} emptyLabel="never" />
                </span>
              </div>
              <p className="font-mono text-[11px] text-muted-foreground">
                item_id: {item.item_id}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onSync(item)}
                disabled={syncingId === item.item_id}
              >
                <RefreshCw
                  className={`h-3.5 w-3.5 ${syncingId === item.item_id ? "animate-spin" : ""}`}
                  aria-hidden
                />
                {syncingId === item.item_id ? "Syncing…" : "Sync now"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onDelete(item)}
                disabled={syncingId === item.item_id}
                aria-label={`Delete ${item.institution_name} connection`}
                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden />
                Delete
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function ErrorCard({ message }: { message: string }) {
  return (
    <Card className="border-destructive/40">
      <CardContent className="flex items-start gap-3 py-6">
        <AlertTriangle
          className="mt-0.5 h-5 w-5 text-destructive"
          aria-hidden
        />
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">
            Couldn't load Plaid status
          </p>
          <p className="font-mono text-xs text-muted-foreground">{message}</p>
        </div>
      </CardContent>
    </Card>
  );
}
